// Waymarks on a hunt map: where the trail leaves for the neighbouring hunts (a worn path out through the trees and a
// signpost naming where it goes), and where the area's landmark stands. Worked out from the same map the server walks
// on (shared/worldmap.ts) without changing it: paths and landmarks only ever cover tiles nothing can walk on (trees,
// rocks, closed-off ground), so a cleared gap in the trees is looks only and the walls stay where they were.
// Pure functions of the map, so the browser and the tests get the same answer.
import { AREA, AREAS, REGIONS, type AreaDef } from '../shared/grounds.ts';
import { T_GROUND, T_ROCK, T_TREE, type WorldMap } from '../shared/worldmap.ts';

export type Edge = 'left' | 'right' | 'top' | 'bottom';
export interface Exit {
  /** The area the path leads to. */
  to: string;
  /** Leaves the region: the sign names the region. */
  region: boolean;
  edge: Edge;
  /** The signpost, in tile units (its foot). */
  sign: { x: number; y: number };
  /** Tile centres from the last open tile out past the map's edge, for the painted path. */
  path: { x: number; y: number }[];
}
export interface Landmark {
  id: string;
  /** Foot of the landmark (bottom middle), in tile units. */
  x: number;
  y: number;
  /** Footprint in tiles: columns x0..x0+w-1, rows y0..y0+h-1. */
  x0: number;
  y0: number;
  w: number;
  h: number;
  /** Tiles whose tree or rock is not drawn while the landmark stands there (its footprint, beside it and in front). */
  hide: Set<number>;
}
export interface Waymarks {
  exits: Exit[];
  landmark: Landmark | null;
  /** Tiles whose tree or rock is not drawn: the gaps the paths go out through. */
  hidden: Set<number>;
}

/**
 * Each landmark's size in tiles, measured on the built art (art/rig3d/props/prop-lm-<area>.ts): how wide its base is
 * and how far it stands up above its foot, by area id. Areas not listed get [3, 5].
 */
const LANDMARK_SIZE: Record<string, [number, number]> = {
  'midgard-1': [3, 4], 'midgard-2': [2, 4], 'midgard-3': [4, 2], 'midgard-4': [4, 4], 'midgard-5': [2, 5], 'midgard-6': [4, 3], 'midgard-7': [4, 3],
  'midgard-8': [4, 4], 'midgard-9': [4, 3], 'midgard-10': [4, 3], 'midgard-11': [4, 3], 'midgard-12': [4, 4], 'midgard-13': [4, 5], 'midgard-14': [4, 4],
  'midgard-15': [4, 4], 'midgard-16': [2, 6], 'midgard-17': [3, 5], 'midgard-18': [4, 3], 'midgard-19': [4, 3], 'hellas-1': [4, 3], 'hellas-2': [3, 5],
  'hellas-3': [4, 3], 'hellas-4': [4, 3], 'hellas-5': [4, 4], 'hellas-6': [3, 4], 'hellas-7': [4, 4], 'hellas-8': [3, 4], 'hellas-9': [3, 4],
  'hellas-10': [3, 4], 'hellas-11': [4, 3], 'hellas-12': [3, 4], 'hellas-13': [4, 3], 'hellas-14': [4, 3], 'hellas-15': [3, 3], 'yomi-1': [4, 4],
  'yomi-2': [4, 4], 'yomi-3': [3, 6], 'yomi-4': [4, 3], 'yomi-5': [3, 4], 'yomi-6': [4, 4], 'yomi-7': [4, 4], 'yomi-8': [3, 4],
  'yomi-9': [4, 4], 'yomi-10': [3, 4], 'yomi-11': [3, 4], 'yomi-12': [3, 4], 'yomi-13': [3, 4], 'yomi-14': [4, 5], 'yomi-15': [4, 4],
};

/** Where each region lies in the world (as on the map page's world inset), for paths that cross into the next one. */
const WORLD: Record<string, [number, number]> = { midgard: [0, 0], hellas: [1, 1], yomi: [1, 0] };

/** The areas next to this one along the trail: the one before and the one after, across region borders too. */
export function trailNeighbours(a: AreaDef): AreaDef[] {
  const i = AREAS.indexOf(a);
  return [AREAS[i - 1], AREAS[i + 1]].filter((n): n is AreaDef => !!n);
}

/** A tile a path or landmark may cover: blocked by a tree or rock, or ground nothing can reach. */
function coverable(m: WorldMap, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= m.w || y >= m.h) return false;
  const i = y * m.w + x, t = m.tiles[i];
  return t === T_TREE || t === T_ROCK || (t === T_GROUND && !m.reach[i]);
}
const open = (m: WorldMap, x: number, y: number) => x >= 0 && y >= 0 && x < m.w && y < m.h && !!m.reach[y * m.w + x];

const EDGES: Edge[] = ['left', 'right', 'top', 'bottom'];
const INWARD: Record<Edge, [number, number]> = { left: [1, 0], right: [-1, 0], top: [0, 1], bottom: [0, -1] };

/** The way out towards a neighbour: the edge the line from the middle of the map meets, nudged along it to a gap. */
function exitFor(m: WorldMap, from: AreaDef, to: AreaDef, taken: Set<number>): Exit | null {
  let dx: number, dy: number;
  if (from.region === to.region) { dx = (to.mx - from.mx) * 1.34; dy = to.my - from.my; }
  else { const a = WORLD[from.region] ?? [0, 0], b = WORLD[to.region] ?? [0, 0]; dx = b[0] - a[0]; dy = b[1] - a[1]; }
  if (!dx && !dy) dy = 1;
  const cx = m.w / 2, cy = m.h / 2, l = Math.hypot(dx, dy);
  // Edges in order of how squarely the neighbour lies past them; on each, the point the ray from the middle meets.
  const facing = (e: Edge) => { const [ix, iy] = INWARD[e]; return -(ix * dx + iy * dy) / l; };
  for (const edge of [...EDGES].sort((a, b) => facing(b) - facing(a))) {
    if (facing(edge) < -0.2) break;
    const side = edge === 'left' || edge === 'right';
    const t = side ? (dx > 0 ? m.w - 1 - cx : -cx) / (dx || 1e-9) : (dy > 0 ? m.h - 1 - cy : -cy) / (dy || 1e-9);
    const along = Math.round(side ? clampN(cy + dy * Math.max(0, t), 0, m.h - 1) : clampN(cx + dx * Math.max(0, t), 0, m.w - 1));
    const span = side ? m.h : m.w;
    const [ix, iy] = INWARD[edge];
    for (let s = 0; s <= span; s++) for (const sgn of s ? [1, -1] : [1]) {
      const a = along + s * sgn;
      if (a < 3 || a > span - 4) continue;
      const bx = edge === 'left' ? 0 : edge === 'right' ? m.w - 1 : a, by = edge === 'top' ? 0 : edge === 'bottom' ? m.h - 1 : a;
      // Walk in until open ground through nothing but trees, rocks and closed-off ground.
      for (let k = 0; k <= 7; k++) {
        const x = bx + ix * k, y = by + iy * k;
        if (open(m, x, y)) {
          if (k === 0) break;
          const cells = Array.from({ length: k }, (_, j) => (by + iy * j) * m.w + (bx + ix * j));
          if (cells.some((c) => taken.has(c))) break;
          // Clear a corridor so the path shows between the trees: a tile either side, and on a path running across
          // the map the two rows below too, whose trees would stand up over it.
          for (let j = 0; j < k; j++) {
            const px = bx + ix * j, py = by + iy * j;
            const near: [number, number][] = side ? [[0, -1], [0, 0], [0, 1], [0, 2]] : [[-1, 0], [0, 0], [1, 0]];
            for (const [ox, oy] of near) if (coverable(m, px + ox, py + oy)) taken.add((py + oy) * m.w + px + ox);
          }
          if (!side) for (const ox of [-1, 0, 1]) if (coverable(m, x + ox, y + 1) && edge === 'top') taken.add((y + 1) * m.w + x + ox);
          cells.forEach((c) => taken.add(c));
          // The path: from the open tile out past the edge.
          const path = [{ x: x + 0.5, y: y + 0.5 }];
          for (let j = k - 1; j >= -1; j--) path.push({ x: bx + ix * j + 0.5, y: by + iy * j + 0.5 });
          // The sign stands beside the path on the open tile, on the side towards the middle of the map.
          const toward = side ? (y < cy ? 1 : -1) : (x < cx ? 1 : -1);
          const sign = side ? { x: x + 0.5 + ix * 0.2, y: y + 0.5 + toward * 0.62 } : { x: x + 0.5 + toward * 0.62, y: y + 0.55 + iy * 0.2 };
          return { to: to.id, region: from.region !== to.region, edge, sign, path };
        }
        if (!coverable(m, x, y)) break;
      }
    }
  }
  return null;
}
const clampN = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** The landmark's spot: a strip of blocked tiles facing open ground, as near the middle of the map as there is one. */
function landmarkFor(m: WorldMap, id: string, [w0, up]: [number, number], taken: Set<number>): Landmark | null {
  const cx = m.w / 2, cy = m.h / 2;
  // Deep and well faced first; then shallower, narrower or less open in front, rather than none.
  const tries: [number, number, number][] = [[w0, 2, 0.6], [w0, 1, 0.6], [w0, 2, 0.01], [w0, 1, 0.01], [w0 - 1, 2, 0.01], [w0 - 1, 1, 0.01], [2, 1, 0.01]]
    .filter(([w]) => w >= 2) as [number, number, number][];
  for (const [w, h, need] of tries) {
    let best: Landmark | null = null, bd = Infinity;
    // its top stays on the map: the foot (row y0 + h) at least `up` rows down
    for (let y0 = Math.max(1, up - h); y0 + h < m.h - 1; y0++) for (let x0 = 1; x0 + w < m.w; x0++) {
      let ok = true;
      for (let y = y0; y < y0 + h && ok; y++) for (let x = x0; x < x0 + w && ok; x++) ok = coverable(m, x, y) && !taken.has(y * m.w + x);
      if (!ok) continue;
      // Open ground in front of it, so it is seen from where the hunt goes on.
      let front = 0;
      for (let x = x0; x < x0 + w; x++) if (open(m, x, y0 + h)) front++;
      if (front < Math.ceil(w * need)) continue;
      const d = Math.hypot(x0 + w / 2 - cx, (y0 + h - cy) * 1.2);
      if (d < bd) { bd = d; best = { id, x: x0 + w / 2, y: y0 + h - 0.1, x0, y0, w, h, hide: new Set() }; }
    }
    if (best) return best;
  }
  return null;
}

const cache = new Map<string, Waymarks>();
/** The waymarks of a hunt map (cached per map). Map keys are area ids; other maps get none. */
export function waymarksFor(m: WorldMap): Waymarks {
  let wm = cache.get(m.key);
  if (wm) return wm;
  const hidden = new Set<number>(), exits: Exit[] = [];
  const area = AREA[m.key];
  let landmark: Landmark | null = null;
  if (area) {
    for (const n of trailNeighbours(area)) { const e = exitFor(m, area, n, hidden); if (e) exits.push(e); }
    // Keep the landmark clear of the paths and their signs.
    const near = new Set(hidden);
    for (const e of exits) for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) near.add((Math.floor(e.sign.y) + dy) * m.w + Math.floor(e.sign.x) + dx);
    const id = `prop-lm-${area.id}`;
    landmark = landmarkFor(m, id, LANDMARK_SIZE[area.id] ?? [3, 5], near);
    if (landmark) {
      // The footprint, and any tree or rock beside it or in the three rows in front that would stand over its base.
      for (let y = landmark.y0; y <= landmark.y0 + landmark.h + 2; y++) for (let x = landmark.x0 - 1; x <= landmark.x0 + landmark.w; x++) if (coverable(m, x, y)) landmark.hide.add(y * m.w + x);
    }
  }
  wm = { exits, landmark, hidden };
  cache.set(m.key, wm);
  return wm;
}

/** What a sign says: the area's name, or the region's when the path leaves it. */
export function signText(e: Exit): string {
  const to = AREA[e.to];
  if (!to) return '';
  return e.region ? REGIONS.find((r) => r.id === to.region)?.name ?? to.name : to.name;
}
